import { widgetByKey, type WidgetDefinition, type WidgetSize } from '@/pages/home/widgets/registry';

/**
 * 홈 위젯의 <b>좌표 배치</b>(배치 커스텀).
 *
 * ## 순서가 아니라 자리다
 *
 * 처음에는 「몇 번째」 로 배치를 적었다. 그러면 위젯 하나의 폭을 바꾸는 순간 뒤의 것이 전부 접혀
 * 들어가며 <b>화면에서의 자리가 달라진다</b> — 「정중앙에 박아 둔 위젯」 이 남의 크기 때문에 움직인다.
 * 그래서 위젯마다 <b>(열, 행) 좌표</b>를 갖는다(사용자 지시 2026-09-16). 아무도 안 건드리면
 * 아무도 안 움직인다.
 *
 * ## 좌표계
 *
 *  · <b>열</b>(`col`) — 한 줄을 100칸으로 쪼갠 격자에서 시작 칸(0~99). 폭도 칸 수(=%)다.
 *    그래서 화면이 좁아져도 <b>한 줄에 몇 개가 보이는지가 변하지 않고</b> 위젯이 같이 줄어든다.
 *  · <b>행</b>(`row`) — {@link ROW_UNIT}px 짜리 행의 번호. 높이는 내용 높이의 몇 %(`heightPercent`)를
 *    이 단위로 환산해 정한다.
 *
 * <b>모바일만 예외</b>다. md(768) 아래에서는 좌표를 무시하고 한 칸씩 세로로 흐른다 — 폰에서 25%는
 * 90px 이고, 그 폭에 남는 글자는 서너 자뿐이다. 그때의 순서는 <b>(행, 열)</b> 이 정한다.
 *
 * ## 겹치면 어떻게 되나 — {@link resolveLayout}
 *
 * 좌표를 사람이 정하면 겹칠 수 있고, 내용이 자라 키가 커져도 겹친다. 그래서 그릴 때마다 한 번
 * 훑어서 <b>겹친 것을 아래로 민다</b>. 위로 끌어올리지는 않는다 — 일부러 비워 둔 자리를 우리가
 * 메워 버리면 그건 좌표 배치가 아니다.
 *
 * <b>핀</b>은 여기서 「밀리지 않는다」 는 뜻이 된다. 좌표가 생긴 뒤에는 모두가 제자리에 있으므로
 * 「그 자리에 둔다」 는 더 이상 핀만의 성질이 아니다. 남는 성질은 <b>양보하지 않는다</b> 쪽이다 —
 * 핀이 박힌 위젯은 먼저 앉고, 겹치면 상대가 내려간다.
 */

/** 한 줄을 몇 칸으로 쪼개는가. 100이면 눈금 하나가 1%다. */
export const LAYOUT_COLUMNS = 100;
/** 격자 한 행의 크기(px). 세로 자리를 이 단위로 잡는다. */
export const ROW_UNIT = 8;
/** 위젯 사이 간격(px). 칸 안쪽 여백 8px + 8px 로 만든다. */
export const GAP = 16;

/** 가로는 <b>한 줄이 천장</b>이다 — 100%가 곧 화면 폭이라 그 위는 화면 밖이다. */
export const WIDTH_MIN = 10;
export const WIDTH_MAX = 100;
/** 세로에는 천장이 없다시피 둔다. 내용의 네 배까지 — 그보다 큰 자리는 스크롤만 길어진다. */
export const HEIGHT_MIN = 20;
export const HEIGHT_MAX = 400;
/** 끌 때 걸리는 눈금(%). 무단계로 두면 48%·52% 같은 값이 쌓여 줄이 안 맞는다. */
const STEP = 5;
/**
 * 눈금에서 벗어나 있어도 <b>붙잡는</b> 값. 3등분(33)·2/3(67)은 5의 배수가 아니라서
 * 자석이 없으면 영영 못 고른다 — 그리고 3등분은 이 배치에서 가장 많이 쓸 폭이다.
 */
const MAGNETS = [25, 33, 50, 67, 75, 100];

export type WidgetSlot = {
  widgetKey: string;
  size: WidgetSize;
  visible: boolean;
  /**
   * 한 줄 대비 가로 배율(%). 없으면 <b>배율 배치를 쓰지 않는다</b>는 뜻이고, 홈은 예전대로
   * 브레이크포인트 격자로 그린다. 배치 커스텀을 한 번도 안 한 사람의 홈이 그대로여야 한다.
   */
  widthPercent?: number;
  /** 내용 높이 대비 세로 배율(%). 100이 내용 그대로다. */
  heightPercent?: number;
  /** 시작 칸(0~99). 없으면 {@link resolveLayout} 이 빈 자리를 찾아 앉힌다. */
  col?: number;
  /** 시작 행({@link ROW_UNIT}px 단위). 없으면 위와 같다. */
  row?: number;
  /** 핀. 박으면 겹쳐도 <b>이쪽이 아니라 상대가</b> 내려간다. */
  pinned?: boolean;
};

/** 홈에 실제로 그릴 위젯 하나 — 정의와 자리. */
export type PlacedWidget = {
  definition: WidgetDefinition;
  size: WidgetSize;
  widthPercent?: number;
  heightPercent?: number;
  col?: number;
  row?: number;
  pinned: boolean;
};

/** 겹침까지 풀고 난 <b>최종</b> 자리. 이 값이 그대로 격자에 꽂힌다. */
export type ResolvedBox = {
  item: PlacedWidget;
  /** 시작 칸(0~99). */
  col: number;
  /** 시작 행. */
  row: number;
  /** 차지하는 칸 수(=%). */
  w: number;
  /** 차지하는 행 수. */
  h: number;
};

/** 보이는 것만 추려 정의를 붙인다. 카탈로그에서 사라진 키는 조용히 버린다. */
export function placeSlots(slots: WidgetSlot[]): PlacedWidget[] {
  return slots
    .filter((slot) => slot.visible)
    .map((slot): PlacedWidget | null => {
      const definition = widgetByKey(slot.widgetKey);
      // 오른쪽 기둥에 서는 위젯은 격자에 넣지 않는다 — `HomePage` 가 따로 세운다.
      if (!definition || definition.placement === 'rail') return null;

      return {
        definition,
        size: slot.size,
        // 서버는 배율·좌표를 안 쓰는 줄을 `null` 로 내려보낸다. `?? undefined` 로 「없음」 을 한 가지
        // 모양으로 모아 둔다 — 두 가지로 두면 «자리가 있는가» 를 보는 곳마다 둘 다 따져야 한다.
        widthPercent: slot.widthPercent ?? undefined,
        heightPercent: slot.heightPercent ?? undefined,
        col: slot.col ?? undefined,
        row: slot.row ?? undefined,
        pinned: slot.pinned ?? false,
      };
    })
    .filter((entry): entry is PlacedWidget => entry !== null);
}

/** 배율 배치가 켜져 있는가. 보이는 위젯 중 하나라도 폭이나 자리를 갖고 있으면 켜진 것이다. */
export function hasCustomLayout(items: PlacedWidget[]): boolean {
  return items.some((item) => item.widthPercent != null || item.col != null);
}

/**
 * 배치 커스텀을 <b>처음 열 때</b> 채워 넣는 폭.
 *
 * 지금 눈에 보이는 그대로에서 시작해야 한다 — 편집을 켜자마자 배치가 흐트러지면 사용자는
 * 자기가 뭘 망가뜨린 줄 안다. 그래서 지금 화면의 열 수(`columnsForViewport`)를 그대로 배율로
 * 옮긴다: 3열이면 33%, 2열이면 50%, 넓은 위젯은 그 두 배.
 *
 * <b>좌표는 여기서 정하지 않는다.</b> 어느 자리가 비어 있는지는 위젯의 키를 재 봐야 알 수 있고,
 * 그건 화면이 한 번 그려진 뒤의 일이다({@link resolveLayout} 이 빈 자리를 찾아 앉힌다).
 */
export function seedLayout(slots: WidgetSlot[], columns: number): WidgetSlot[] {
  const unit = Math.max(WIDTH_MIN, Math.round(100 / Math.max(1, columns)));

  return slots.map((slot) => {
    if (!slot.visible || slot.widthPercent != null) return slot;

    return {
      ...slot,
      widthPercent: slot.size === 'wide' ? Math.min(WIDTH_MAX, unit * 2) : unit,
      heightPercent: 100,
    };
  });
}

/** 배율과 자리를 통째로 걷어내 기본(브레이크포인트) 배치로 되돌린다. */
export function stripLayout(slots: WidgetSlot[]): WidgetSlot[] {
  return slots.map((slot) => ({ widgetKey: slot.widgetKey, size: slot.size, visible: slot.visible }));
}

/** 지금 화면이 몇 열인가 — `WidgetMasonry` 의 기본 격자와 같은 지점에서 갈린다. */
export function columnsForViewport(): number {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return 2;
  return window.matchMedia('(min-width: 1536px)').matches ? 3 : 2;
}

export function snapWidth(value: number): number {
  return snap(value, WIDTH_MIN, WIDTH_MAX);
}

export function snapHeight(value: number): number {
  return snap(value, HEIGHT_MIN, HEIGHT_MAX);
}

/** 끌어 옮길 때 시작 칸이 걸리는 눈금. 폭과 같은 5칸이라야 위젯끼리 옆면이 맞는다. */
export function snapColumn(value: number, width: number): number {
  const limit = LAYOUT_COLUMNS - width;
  const stepped = Math.round(value / STEP) * STEP;
  // 오른쪽 끝에 붙이는 일은 눈금보다 흔하다 — 폭이 33이면 끝 칸은 67이라 5의 배수가 아니다.
  const snapped = Math.abs(value - limit) <= 2 ? limit : stepped;
  return clampInt(snapped, 0, Math.max(0, limit));
}

function snap(value: number, min: number, max: number): number {
  const clamped = Math.min(max, Math.max(min, value));
  const magnet = MAGNETS.find((candidate) => candidate >= min && candidate <= max && Math.abs(clamped - candidate) <= 2);
  return magnet ?? Math.round(clamped / STEP) * STEP;
}

function clampInt(value: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, Math.round(value)));
}

/**
 * 좌표와 실측 높이로 <b>최종 자리</b>를 낸다. 그리기 직전에 매번 돈다.
 *
 * <pre>
 *   1. 붙박이부터 앉힌다 — 지금 끌고 있는 것, 그다음 핀
 *   2. 자리를 가진 나머지를 (행, 열) 순으로 앉히되, 겹치면 <b>아래로</b> 민다
 *   3. 자리가 없는 것(새로 켠 위젯·옛 배치)은 맨 마지막에 빈 자리를 찾아 앉는다
 * </pre>
 *
 * 위로는 절대 당기지 않는다. 일부러 비워 둔 자리를 메우면 좌표 배치가 아니게 된다.
 *
 * <p>끌고 있는 것(`held`)을 맨 앞에 두는 이유: 끄는 동안 <b>손끝이 정답</b>이어야 한다. 이것도
 * 밀리게 두면 놓으려는 자리에 다른 위젯이 있을 때 카드가 손에서 달아난다.
 */
export function resolveLayout(
  items: PlacedWidget[],
  naturalHeights: Record<string, number>,
  held?: string | null,
): ResolvedBox[] {
  const sized = items.map((item) => {
    const key = item.definition.widgetKey;
    const w = clampInt(item.widthPercent ?? LAYOUT_COLUMNS, 1, LAYOUT_COLUMNS);
    const wanted = ((naturalHeights[key] ?? 0) * (item.heightPercent ?? 100)) / 100;
    // 칸 높이 = 자리 + 안쪽 여백(위아래 8px). 그래서 이웃 칸과 정확히 16px 이 벌어진다.
    const h = Math.max(1, Math.ceil((wanted + GAP) / ROW_UNIT));
    return { item, key, w, h, rank: rankOf(item, key, held) };
  });

  const queue = [...sized].sort((a, b) => {
    if (a.rank !== b.rank) return a.rank - b.rank;
    const rowA = a.item.row ?? Number.MAX_SAFE_INTEGER;
    const rowB = b.item.row ?? Number.MAX_SAFE_INTEGER;
    if (rowA !== rowB) return rowA - rowB;
    return (a.item.col ?? 0) - (b.item.col ?? 0);
  });

  const placed: ResolvedBox[] = [];
  for (const entry of queue) {
    const seated = seat(entry, placed);
    placed.push({ item: entry.item, col: seated.col, row: seated.row, w: entry.w, h: entry.h });
  }

  // (행, 열) 순으로 돌려준다 — 모바일 1열이 이 순서를 그대로 쓴다. 넓은 화면에서는 좌표가
  // 자리를 정하므로 DOM 순서가 화면을 바꾸지 않지만, 읽는 순서(탭 이동·스크린리더)는 이게 맞다.
  return placed.sort((a, b) => a.row - b.row || a.col - b.col);
}

/** 0 = 끌고 있는 것, 1 = 핀, 2 = 자리가 있는 것, 3 = 자리가 없는 것. */
function rankOf(item: PlacedWidget, key: string, held?: string | null): number {
  if (held === key) return 0;
  if (item.pinned) return 1;
  return item.col != null && item.row != null ? 2 : 3;
}

function seat(
  entry: { item: PlacedWidget; w: number; h: number; rank: number },
  placed: ResolvedBox[],
): { col: number; row: number } {
  // 자리를 가진 적이 없으면 빈 곳을 찾아 준다. 옛 배치를 배율 배치로 옮기는 길이기도 하다.
  if (entry.item.col == null || entry.item.row == null) return firstFreeSpot(entry.w, entry.h, placed);

  const col = clampInt(entry.item.col, 0, Math.max(0, LAYOUT_COLUMNS - entry.w));
  const row = Math.max(0, Math.round(entry.item.row));
  // 붙박이(끌고 있는 것·핀)는 겹치든 말든 여기 앉는다. 비키는 쪽은 상대다.
  if (entry.rank <= 1) return { col, row };

  return { col, row: pushDown(col, row, entry.w, entry.h, placed) };
}

function pushDown(col: number, row: number, w: number, h: number, placed: ResolvedBox[]): number {
  let next = row;
  let moved = true;

  // 한 번 내려가면 그 아래에서 또 걸릴 수 있다. 더 이상 안 걸릴 때까지 되풀이한다.
  while (moved) {
    moved = false;
    for (const box of placed) {
      if (!overlaps(col, next, w, h, box)) continue;
      next = box.row + box.h;
      moved = true;
    }
  }
  return next;
}

/**
 * 비어 있는 첫 자리.
 *
 * 모든 칸을 훑지 않고 <b>이미 놓인 것의 모서리</b>만 후보로 본다 — 빈 자리의 왼쪽 위 모서리는
 * 언제나 다른 위젯의 모서리이거나 격자의 시작점이다. 100×수백 칸을 전부 훑으면 위젯이 데이터를
 * 받아 키가 자랄 때마다 그 계산이 다시 돈다.
 */
function firstFreeSpot(w: number, h: number, placed: ResolvedBox[]): { col: number; row: number } {
  const rows = unique([0, ...placed.map((box) => box.row + box.h)]);
  const cols = unique([0, ...placed.map((box) => box.col), ...placed.map((box) => box.col + box.w)]);

  for (const row of rows) {
    for (const col of cols) {
      if (col + w > LAYOUT_COLUMNS) continue;
      if (placed.some((box) => overlaps(col, row, w, h, box))) continue;
      return { col, row };
    }
  }

  return { col: 0, row: placed.reduce((bottom, box) => Math.max(bottom, box.row + box.h), 0) };
}

function overlaps(col: number, row: number, w: number, h: number, box: ResolvedBox): boolean {
  return col < box.col + box.w && box.col < col + w && row < box.row + box.h && box.row < row + h;
}

function unique(values: number[]): number[] {
  return [...new Set(values)].sort((a, b) => a - b);
}
